import { create } from "zustand";

/**
 * The three things a player is allowed to change, and where they are kept.
 * Outside `ui/` because the input layer, the audio layer and the renderer read
 * them and may not import the HUD store; zustand only so a slider in the DOM
 * and a value cached in a mousemove handler are the same number.
 *
 * IMPORT-SAFE UNDER PLAIN NODE: the headless smoke scripts reach this file via
 * `render/shadows.ts`, so every storage access is guarded and module init can
 * never throw — a node import gets the defaults. Persistence is a side effect
 * of setting: every setter writes the whole record to one `localStorage` key,
 * and a failed write is swallowed.
 */

/**
 * The one key, VERSIONED IN THE NAME: an incompatible future shape gets `.v2`
 * rather than an in-place migration.
 */
const STORAGE_KEY = "rietz.settings.v1";

/**
 * Degrees of yaw per mouse COUNT at sensitivity 1 — the Counter-Strike 2
 * constant, so a player's number means what it means there. Under pointer lock
 * with `unadjustedMovement: true`, `movementX` is 1:1 with counts.
 */
const DEG_PER_COUNT = 0.022;

/** CS2's own default. */
export const DEFAULT_SENSITIVITY = 2.5;

/**
 * The usable range: 0.1 is a full turn in 16 000 counts, 8 in about 2 000.
 * Clamped on load AND on set, so neither a hand-edited entry nor a typed number
 * can produce a session the player cannot look around in.
 */
export const MIN_SENSITIVITY = 0.1;
export const MAX_SENSITIVITY = 8;

/** 50 is TODAY'S loudness exactly — see `MASTER_GAIN` in `audio/synth.ts`. */
export const DEFAULT_VOLUME = 50;
export const MIN_VOLUME = 0;
export const MAX_VOLUME = 100;

/** The persisted record — plain data, the whole of what the key holds. */
export interface SettingsValues {
  /** Counter-Strike 2 scale. See `DEG_PER_COUNT`. */
  sensitivity: number;
  /** 0..100, integer. 50 is unity. */
  volume: number;
  /**
   * Real-time shadow maps. Read ONCE at module load by `render/shadows.ts`, so
   * a change takes effect on the next page load.
   */
  shadows: boolean;
}

export const DEFAULT_SETTINGS: SettingsValues = {
  sensitivity: DEFAULT_SENSITIVITY,
  volume: DEFAULT_VOLUME,
  shadows: false,
};

// ---------------------------------------------------------------------------
// The pure half. No store, no storage — unit-testable on its own.
// ---------------------------------------------------------------------------

/**
 * Radians of yaw per mouse count — the whole conversion in one place: CS units
 * → degrees → radians. The old hardcoded `0.0022 rad/px` corresponds to CS2 ≈
 * 5.73.
 */
export function radPerCount(sensitivity: number): number {
  return (sensitivity * DEG_PER_COUNT * Math.PI) / 180;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** One number out of untrusted JSON, or the default when it is not one. */
function num(raw: unknown, fallback: number): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : fallback;
}

/**
 * Anything at all → a usable settings record. The input is UNTRUSTED (whatever
 * was in `localStorage`); each field is taken on its own, so one bad value
 * costs one setting.
 */
export function sanitizeSettings(raw: unknown): SettingsValues {
  const o = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<
    Record<keyof SettingsValues, unknown>
  >;
  return {
    sensitivity: clamp(
      num(o.sensitivity, DEFAULT_SENSITIVITY),
      MIN_SENSITIVITY,
      MAX_SENSITIVITY,
    ),
    volume: Math.round(clamp(num(o.volume, DEFAULT_VOLUME), MIN_VOLUME, MAX_VOLUME)),
    shadows: o.shadows === true,
  };
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

/**
 * `localStorage`, or `null` where there is none: plain node has no global, and
 * a browser blocking all cookies THROWS on the property access.
 */
function storage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function loadSettings(): SettingsValues {
  const store = storage();
  if (store === null) return { ...DEFAULT_SETTINGS };
  let text: string | null;
  try {
    text = store.getItem(STORAGE_KEY);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
  if (text === null) return { ...DEFAULT_SETTINGS };
  try {
    return sanitizeSettings(JSON.parse(text));
  } catch {
    // Not JSON at all. The defaults are a better answer than a crash on boot,
    // and the next set() overwrites the garbage.
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(values: SettingsValues): void {
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Quota, private mode, a disabled origin. Losing the preference on the next
    // reload is the whole cost; the running session is unaffected.
  }
}

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

export interface SettingsState extends SettingsValues {
  setSensitivity(sensitivity: number): void;
  setVolume(volume: number): void;
  setShadows(shadows: boolean): void;
}

/**
 * THE settings, live. The panel subscribes with the hook; `input.ts` and
 * `main.ts` take `getState()` once and `subscribe()` — a mousemove handler must
 * not read a store per event. Every setter SANITIZES and then PERSISTS, so the
 * panel can bind a number field straight to it.
 */
export const useSettings = create<SettingsState>((set, get) => {
  const commit = (patch: Partial<SettingsValues>): void => {
    const next = sanitizeSettings({ ...currentValues(get()), ...patch });
    set(next);
    saveSettings(next);
  };
  return {
    ...loadSettings(),
    setSensitivity: (sensitivity) => {
      commit({ sensitivity });
    },
    setVolume: (volume) => {
      commit({ volume });
    },
    setShadows: (shadows) => {
      commit({ shadows });
    },
  };
});

/** The persisted fields of the live state, without the setters riding along. */
function currentValues(s: SettingsState): SettingsValues {
  return {
    sensitivity: s.sensitivity,
    volume: s.volume,
    shadows: s.shadows,
  };
}
